import React, {useEffect, useMemo} from "react"
import {connect} from "react-redux"
import {Link} from "react-router-dom"
import {ColumnDef} from "@tanstack/react-table"
import {Badge, ButtonGroup, IconButton, Tooltip} from "@chakra-ui/react"
import {CheckCircleIcon, PencilIcon, PresentationChartLineIcon, TrashIcon} from "@heroicons/react/24/outline"

import {IUser} from "@backend/types"
import {useFilter} from "@hooks/useFilter"
import {Card, Loading, Pagination, SearchInput, Table} from "@components/index"

import {UserInfo} from "../../components"
import {userActions, userApiCalls} from "../../service"


function UserList({userReducer: {users}, list}) {
    const {filter, onFilterChange, setFilter} = useFilter<IUser>(userApiCalls.apiCalls)

    const columns = useMemo<ColumnDef<IUser>[]>(
        () => [
            {
                header: "Display Name",
                accessorKey: "publicInfo.displayName",
                enableColumnFilter: false,
                cell: ({row: {original}}) => <UserInfo user={original}/>
            },
            {
                header: "Organization",
                accessorKey: "work.organization",
                enableColumnFilter: false
            },
            {
                header: "Approve Status",
                accessorKey: "security.isConfirmed",
                enableSorting: false,
                meta: {
                    true: "Approved",
                    false: "Not Approved"
                },
                cell: ({row: {original}, getValue}) =>
                    <Badge colorScheme={getValue() ? "green" : "red"}>
                        {getValue() ? "Approved" : "Not Approved"}
                    </Badge>
            },
            {
                header: "Actions",
                id: "actions",
                cell: ({row: {original}}) => <div>
                    <ButtonGroup variant="ghost" spacing="6" size="sm">
                        <Tooltip label="Stats" placement="top" openDelay={300}>
                            <IconButton
                                as={Link}
                                to={`stats/${original._id.toString()}`}
                                aria-label="User Game Plays Stats"
                                icon={<PresentationChartLineIcon className="w-4 h-4"/>}
                                colorScheme="yellow">Stats</IconButton>
                        </Tooltip>
                        <Tooltip label="Edit" placement="top" openDelay={300}>
                            <IconButton
                                as={Link}
                                to={original._id.toString()}
                                aria-label="Edit User"
                                icon={<PencilIcon className="w-4 h-4"/>}
                                colorScheme="blue">Edit</IconButton>
                        </Tooltip>
                        <Tooltip label="Delete" placement="top" openDelay={300}>
                            <IconButton
                                aria-label="Delete User"
                                isDisabled
                                onClick={() => {
                                    // setItemId(original._id.toString())
                                    // onOpen()
                                }}
                                icon={<TrashIcon className="w-4 h-4"/>}
                                colorScheme="red">Delete User</IconButton>
                        </Tooltip>
                        {!original.security.isConfirmed &&
                          <Tooltip label="Approve User" placement="top" openDelay={300}>
                            <IconButton
                              aria-label="Approve User"
                              icon={<CheckCircleIcon className="w-4 h-4"/>}
                              onClick={() => {
                                  onApprove(original._id)
                              }}
                              colorScheme="green">Approve User</IconButton>
                          </Tooltip>}
                    </ButtonGroup>
                </div>
            }
        ], [])

    useEffect(() => {
        if (!users)
            list()
    }, [])

    function onApprove(userId) {
        userApiCalls.approve(userId).then(list)
    }

    if (!users)
        return <Loading/>

    return <Card
        bodyClassName="p-0"
        title="Users"
        search={<SearchInput
            placeholder="Search by display name or email"
            onSearch={userApiCalls.apiCalls.listWithPager}
            cb={setFilter}/>}
        footer={(filter?.results || users).totalPages > 1 &&
          <Pagination
            page={filter?.query?.page || 0}
            pageCount={(filter?.results || users).totalPages}
            onPageChange={onFilterChange}
          />}
    >
        <Table
            isLoading={filter?.isLoading}
            columns={columns}
            onFilterChange={onFilterChange}
            sort={[{
                id: "createdAt",
                desc: true
            }]}
            data={(filter?.results || users || {}).docs}
        />
    </Card>
}

export default connect(({userReducer}) => ({userReducer}), {
    list: userActions.actions.listWithPager,
    userDelete: userActions.actions.delete
})(UserList)